import {
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  BoxGeometry,
} from "three";
import type { TrackModel } from "@midnight-pass/sim";

/** Flat night colours (SPEC §9.1). Visual polish comes in Phase 6. */
const COLOURS = {
  road: "#1a1e30",
  centreLine: "#7a86ad",
  edgeLine: "#00b8c4",
  kerbA: "#ff184c",
  kerbB: "#dfe6ff",
  wall: "#252c45",
  wallTop: "#00f0ff",
  pole: "#20263a",
  lamp: "#ffdc8a",
};

const KERB_WIDTH = 1.1;
const EDGE_LINE_INSET = 1.5;
const EDGE_LINE_WIDTH = 0.16;
const CENTRE_LINE_WIDTH = 0.22;
const DASH_LENGTH = 6;
const WALL_HEIGHT = 1.1;
const WALL_CAP = 0.35;
const LAMP_SPACING = 36;
const LAMP_HEIGHT = 7;
const LAMP_OFFSET = 2.2;

interface Builder {
  positions: number[];
  colours: number[];
  indices: number[];
}

function newBuilder(): Builder {
  return { positions: [], colours: [], indices: [] };
}

function toMesh(b: Builder): Mesh {
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(b.positions, 3));
  g.setAttribute("color", new Float32BufferAttribute(b.colours, 3));
  g.setIndex(b.indices);
  return new Mesh(g, new MeshBasicMaterial({ vertexColors: true, side: DoubleSide }));
}

/** Add a quad from four corners (in order around the quad) with one colour. */
function quad(b: Builder, p: number[][], colour: Color): void {
  const base = b.positions.length / 3;
  for (const v of p) {
    b.positions.push(v[0]!, v[1]!, v[2]!);
    b.colours.push(colour.r, colour.g, colour.b);
  }
  b.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

/**
 * Road, kerbs, lines and walls from the track's sampled centre-line: the same arrays the sim
 * collides against, so what you see is what you hit.
 */
export function buildCourse(track: TrackModel): Group {
  const group = new Group();
  const road = newBuilder();
  const marks = newBuilder();
  const kerbs = newBuilder();
  const walls = newBuilder();

  const colour = (hex: string) => new Color(hex);
  const cRoad = colour(COLOURS.road);
  const cCentre = colour(COLOURS.centreLine);
  const cEdge = colour(COLOURS.edgeLine);
  const cKerbA = colour(COLOURS.kerbA);
  const cKerbB = colour(COLOURS.kerbB);
  const cWall = colour(COLOURS.wall);
  const cWallTop = colour(COLOURS.wallTop);

  /** World point at sample `i`, `off` metres left of the centre-line and `h` above the road. */
  const at = (i: number, off: number, h: number): number[] => [
    track.x[i]! + track.tz[i]! * off,
    track.y[i]! + h,
    track.z[i]! - track.tx[i]! * off,
  ];

  for (let i = 0; i < track.count - 1; i++) {
    const j = i + 1;
    const half = Math.min(track.width[i]!, track.width[j]!) / 2;
    const halfI = track.width[i]! / 2;
    const halfJ = track.width[j]! / 2;

    quad(road, [at(i, halfI, 0), at(i, -halfI, 0), at(j, -halfJ, 0), at(j, halfJ, 0)], cRoad);

    // Dashed centre-line.
    if (Math.floor(track.dist[i]! / DASH_LENGTH) % 2 === 0) {
      const w = CENTRE_LINE_WIDTH / 2;
      quad(marks, [at(i, w, 0.02), at(i, -w, 0.02), at(j, -w, 0.02), at(j, w, 0.02)], cCentre);
    }

    // Edge lines and kerbs on both sides.
    for (const side of [1, -1]) {
      const lineOff = side * (half - EDGE_LINE_INSET);
      const lw = EDGE_LINE_WIDTH / 2;
      quad(
        marks,
        [
          at(i, lineOff + lw, 0.02),
          at(i, lineOff - lw, 0.02),
          at(j, lineOff - lw, 0.02),
          at(j, lineOff + lw, 0.02),
        ],
        cEdge,
      );

      const kerbColour = Math.floor(track.dist[i]! / 3) % 2 === 0 ? cKerbA : cKerbB;
      const oi = side * halfI;
      const oj = side * halfJ;
      quad(
        kerbs,
        [
          at(i, oi, 0.03),
          at(i, oi - side * KERB_WIDTH, 0.03),
          at(j, oj - side * KERB_WIDTH, 0.03),
          at(j, oj, 0.03),
        ],
        kerbColour,
      );

      // Wall: inner face on the road edge, plus a glowing cap.
      quad(
        walls,
        [at(i, oi, 0), at(i, oi, WALL_HEIGHT), at(j, oj, WALL_HEIGHT), at(j, oj, 0)],
        cWall,
      );
      quad(
        walls,
        [
          at(i, oi, WALL_HEIGHT),
          at(i, oi + side * WALL_CAP, WALL_HEIGHT),
          at(j, oj + side * WALL_CAP, WALL_HEIGHT),
          at(j, oj, WALL_HEIGHT),
        ],
        cWallTop,
      );
    }
  }

  group.add(toMesh(road), toMesh(marks), toMesh(kerbs), toMesh(walls));
  group.add(...buildLamps(track));
  return group;
}

/** Street lamps outside the walls: cheap instanced boxes that give a strong sense of speed. */
function buildLamps(track: TrackModel): InstancedMesh[] {
  const count = Math.floor(track.length / LAMP_SPACING) * 2;
  const poles = new InstancedMesh(
    new BoxGeometry(0.25, LAMP_HEIGHT, 0.25),
    new MeshBasicMaterial({ color: COLOURS.pole }),
    count,
  );
  const heads = new InstancedMesh(
    new BoxGeometry(0.9, 0.25, 0.9),
    new MeshBasicMaterial({ color: COLOURS.lamp }),
    count,
  );
  const m = new Matrix4();
  let n = 0;
  for (let d = LAMP_SPACING; d < track.length - 1 && n < count; d += LAMP_SPACING) {
    const i = Math.min(track.count - 1, Math.round(d / track.tuning.sampleSpacing));
    const half = track.width[i]! / 2 + LAMP_OFFSET;
    for (const side of [1, -1]) {
      if (n >= count) break;
      const x = track.x[i]! + track.tz[i]! * half * side;
      const z = track.z[i]! - track.tx[i]! * half * side;
      m.makeTranslation(x, track.y[i]! + LAMP_HEIGHT / 2, z);
      poles.setMatrixAt(n, m);
      m.makeTranslation(x, track.y[i]! + LAMP_HEIGHT, z);
      heads.setMatrixAt(n, m);
      n++;
    }
  }
  poles.count = n;
  heads.count = n;
  return [poles, heads];
}
